import React, { useEffect, useState, useRef } from 'react';
import { ExternalLink } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { OnboardingStepProps } from '../onboardingSteps';
import {
  fetchMasterPlatforms,
  fetchUserPlatforms,
  createUserPlatform,
  updateUserPlatform,
} from '../onboardingApi';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  buildPlatformUrl,
  buildVisitUrl,
  extractHandleForDisplay,
  hostFromUrl,
} from '@/lib/platformUrl';

interface MasterPlatform {
  _id: string;
  title: string;
  baseURL: string;
  platformType: 'social-media' | 'audio-streaming';
  image: string;
  isActive: boolean;
}

interface PlatformEntry {
  platformId: string;
  platformName: string;
  baseURL: string;
  image: string;
  handle: string;
  platformType: 'social-media' | 'audio-streaming';
  alreadyExists: boolean;
  autoLinked?: boolean;
  userPlatformId?: string;
  originalHandle?: string;
  /** Canonical URL from the API (already correct after prefill). */
  url?: string;
}

/** Return true only if the handle looks like a real username */
function isValidHandle(h: string): boolean {
  const t = h.trim();
  if (!t || t === '#' || t === '/' || t === '@') return false;
  if (t.startsWith('http://') || t.startsWith('https://')) return false;
  if (/^[#@\/\s]+$/.test(t)) return false;
  return true;
}

/* ─── Sortable card wrapper ─────────────────────────────────── */
function SortableCard({
  entry,
  onHandleChange,
}: {
  entry: PlatformEntry;
  onHandleChange: (platformId: string, value: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.platformId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`p-3 space-y-2 rounded-lg shadow-none ${
        entry.handle.trim()
          ? 'border border-border'
          : 'border-2 border-dashed border-border'
      }`}
    >
      {/* Platform header */}
      <div className="flex items-center gap-2">
        {/* Drag handle */}
        <div
          className="cursor-grab active:cursor-grabbing text-muted-foreground/60 hover:text-muted-foreground shrink-0 touch-none"
          {...attributes}
          {...listeners}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="9" cy="5" r="1.5" />
            <circle cx="15" cy="5" r="1.5" />
            <circle cx="9" cy="12" r="1.5" />
            <circle cx="15" cy="12" r="1.5" />
            <circle cx="9" cy="19" r="1.5" />
            <circle cx="15" cy="19" r="1.5" />
          </svg>
        </div>
        <div className="w-7 h-7 flex items-center justify-center rounded bg-secondary/50 p-1 shrink-0">
          <img src={entry.image} alt={entry.platformName} className="w-full h-full object-contain" />
        </div>
        <span className="text-sm font-medium text-foreground">{entry.platformName}</span>
        {entry.handle.trim() && (() => {
          // Prefer the canonical URL already on the entry (correct after
          // prefill). Fall back to a freshly-built URL only when the entry
          // has no stored URL — this is what makes the link future-proof
          // for platforms with multiple URL shapes (e.g. YouTube).
          const visitUrl = buildVisitUrl(entry) ||
            buildPlatformUrl({ title: entry.platformName, baseURL: entry.baseURL }, entry.handle);
          if (!visitUrl) return null;
          return (
            <a
              href={visitUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto text-xs text-primary hover:text-primary/80 hover:underline inline-flex items-center gap-1"
              title={`Visit ${entry.platformName} profile to verify the handle is correct`}
            >
              <span>Visit profile</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          );
        })()}

      </div>

      {/* Handle input with baseURL prefix */}
      <Input
        prefix={<span className="font-mono text-xs whitespace-nowrap">{entry.baseURL}</span>}
        value={entry.handle}
        onChange={(e) => onHandleChange(entry.platformId, e.target.value)}
        className="font-mono"
        placeholder="username"
      />
    </Card>
  );
}

/* ─── Main component ────────────────────────────────────────── */
const PlatformsOnboarding: React.FC<OnboardingStepProps> = ({ saveRef, onValidationChange, onPreviewChange, buildResult, backgroundStatus }) => {
  const [activeTab, setActiveTab] = useState<'social-media' | 'audio-streaming'>('social-media');
  const [entries, setEntries] = useState<PlatformEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Map master-platform titles to the buildResult key that holds their prefilled URL.
  // Most platforms live under `socialLinks`, but Wikipedia is delivered on a separate
  // `buildResult.wikipedia` object, so it gets its own sentinel key handled in the
  // prefill loop below.
  //
  // The backend discovers platforms modularly by hostname, so keys here must
  // match both the MasterPlatforms title and the canonical host used in
  // `buildResult.socialLinks`.
  const socialLinkKeyMap: Record<string, string> = {
    instagram: 'instagram',
    twitter: 'twitter',
    'x': 'twitter',
    'x.com': 'twitter',
    facebook: 'facebook',
    youtube: 'youtube',
    'youtube music': 'youtube music',
    spotify: 'spotify',
    soundcloud: 'soundcloud',
    'apple music': 'apple music',
    'jiosaavn': 'jiosaavn',
    'gaana': 'gaana',
    'amazon music': 'amazon music',
    wikipedia: 'wikipedia',
  };

  // Sentinel value for the key in `socialLinkKeyMap` that means "read from
  // buildResult.wikipedia.url instead of buildResult.socialLinks".
  const WIKI_SENTINEL_KEY = 'wikipedia';

  // Load data on mount
  useEffect(() => {
    const load = async () => {
      try {
        const [masters, socialExisting, audioExisting] = await Promise.all([
          fetchMasterPlatforms(),
          fetchUserPlatforms('social-media'),
          fetchUserPlatforms('audio-streaming'),
        ]);
        const allMasters = Array.isArray(masters) ? (masters as MasterPlatform[]) : [];
        const activeMasters = allMasters.filter((p) => p.isActive);

        const allExisting = [
          ...(Array.isArray(socialExisting) ? socialExisting : []),
          ...(Array.isArray(audioExisting) ? audioExisting : []),
        ] as Array<{
          _id: string;
          platformId: { _id: string; title: string; baseURL: string; image: string } | string;
          platformType: string;
          handle: string;
        }>;

        // Build a map of existing platform handles + their DB ids
        const existingMap = new Map<string, { handle: string; userPlatformId: string }>();
        allExisting.forEach((p) => {
          const pid = p.platformId && typeof p.platformId === 'object' ? p.platformId._id : (p.platformId as string);
          if (pid) existingMap.set(pid, { handle: p.handle || '', userPlatformId: p._id });
        });

        // Build entries from all active master platforms
        const platformEntries: PlatformEntry[] = activeMasters.map((mp) => {
          const existing = existingMap.get(mp._id);
          const isExisting = !!existing;
          let handle = existing?.handle || '';
          const userPlatformId = existing?.userPlatformId;

          // If not already saved in DB, try to prefill from buildResult.
          // Most platforms live under buildResult.socialLinks, but Wikipedia
          // is returned as a separate buildResult.wikipedia object.
          let autoLinked = false;
          if (!isExisting) {
            const linkKey = socialLinkKeyMap[mp.title.toLowerCase()];
            const fallbackKey = hostFromUrl(mp.baseURL);
            let linkUrl: string | null | undefined = null;
            if (linkKey === WIKI_SENTINEL_KEY) {
              linkUrl = buildResult?.wikipedia?.url || null;
            } else if (linkKey) {
              linkUrl = buildResult?.socialLinks?.[linkKey] ?? null;
              // Defensive: backend may have stored it under the canonical host
              if (!linkUrl && fallbackKey && fallbackKey !== linkKey) {
                linkUrl = buildResult?.socialLinks?.[fallbackKey] ?? null;
              }
            }
            if (linkUrl) {
              // Extract a handle that round-trips through buildPlatformUrl.
              // The previous split-on-baseURL logic produced handles like
              // "artist/5C1S..." for Spotify and broke on www. prefixes.
              handle = extractHandleForDisplay(mp, linkUrl);
              if (handle) autoLinked = true;
            }
          }

          // If already saved via prefillService AND buildResult had this platform, mark as auto-linked
          if (isExisting && buildResult) {
            const linkKey = socialLinkKeyMap[mp.title.toLowerCase()];
            const fallbackKey = hostFromUrl(mp.baseURL);
            if (linkKey === WIKI_SENTINEL_KEY && buildResult.wikipedia?.url) {
              autoLinked = true;
            } else if (
              linkKey &&
              (buildResult.socialLinks?.[linkKey] ||
                (fallbackKey && buildResult.socialLinks?.[fallbackKey]))
            ) {
              autoLinked = true;
            }
          }

          // Clear handle if it doesn't look like a real username
          if (handle && !isValidHandle(handle)) handle = '';

          return {
            platformId: mp._id,
            platformName: mp.title,
            baseURL: mp.baseURL,
            image: mp.image,
            handle,
            platformType: mp.platformType,
            alreadyExists: isExisting,
            autoLinked,
            userPlatformId,
            originalHandle: handle,
          };
        });

        setEntries(platformEntries);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Always valid — step is optional
  useEffect(() => {
    onValidationChange(true);
  }, [onValidationChange]);

  useEffect(() => {
    if (loading) return;
    const previewEntries = entries
      .filter((entry) => entry.handle.trim())
      .map((entry) => ({
        _id: entry.userPlatformId || `onboarding-${entry.platformId}`,
        platformId: {
          _id: entry.platformId,
          title: entry.platformName,
          baseURL: entry.baseURL,
          image: entry.image,
        },
        platformName: entry.platformName,
        platformType: entry.platformType,
        handle: entry.handle,
        url: buildVisitUrl(entry) || buildPlatformUrl({ title: entry.platformName, baseURL: entry.baseURL }, entry.handle),
        isActive: true,
      }));
    onPreviewChange?.({
      platforms: {
        social: previewEntries.filter((entry) => entry.platformType === 'social-media'),
        streaming: previewEntries.filter((entry) => entry.platformType === 'audio-streaming'),
      },
    });
  }, [loading, entries, onPreviewChange]);

  // ── Re-fetch when background social discovery completes ──
  // The initial mount fetch runs immediately after /build returns, before
  // the background task has written UserPlatforms to the DB. When
  // socialReady flips from false → true we re-fetch and merge, but only
  // update entries the user hasn't manually edited (handle === originalHandle).
  const prevSocialReadyRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    const prev = prevSocialReadyRef.current;
    prevSocialReadyRef.current = backgroundStatus?.socialReady;
    // Only fire on the rising edge (false → true), not on mount.
    if (prev === false && backgroundStatus?.socialReady && entries.length > 0) {
      const refetchAndMerge = async () => {
        try {
          const [socialExisting, audioExisting] = await Promise.all([
            fetchUserPlatforms('social-media'),
            fetchUserPlatforms('audio-streaming'),
          ]);
          const allExisting = [
            ...(Array.isArray(socialExisting) ? socialExisting : []),
            ...(Array.isArray(audioExisting) ? audioExisting : []),
          ] as Array<{
            _id: string;
            platformId: { _id: string; title: string; baseURL: string; image: string } | string;
            platformType: string;
            handle: string;
            url?: string;
          }>;

          // Build a map of DB entries keyed by master platform ID
          const dbMap = new Map<string, { handle: string; userPlatformId: string; url?: string }>();
          allExisting.forEach((p) => {
            const pid = p.platformId && typeof p.platformId === 'object' ? p.platformId._id : (p.platformId as string);
            if (pid) dbMap.set(pid, { handle: p.handle || '', userPlatformId: p._id, url: p.url });
          });

          // Merge DB entries into existing entries — only update entries
          // where the user hasn't manually edited the handle.
          setEntries((prev) =>
            prev.map((e) => {
              const dbEntry = dbMap.get(e.platformId);
              if (!dbEntry) return e; // no DB entry for this platform
              const userHasEdited = e.handle !== (e.originalHandle || '');
              if (userHasEdited) return e; // don't overwrite user edits
              // DB has this platform — adopt the DB values
              return {
                ...e,
                handle: dbEntry.handle,
                originalHandle: dbEntry.handle,
                alreadyExists: true,
                userPlatformId: dbEntry.userPlatformId,
                url: dbEntry.url,
                autoLinked: true,
              };
            })
          );
        } catch {
          // non-fatal — entries stay as-is from buildResult fallback
        }
      };
      refetchAndMerge();
    }
  }, [backgroundStatus?.socialReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save platform handles on continue — create new, update changed existing.
  // Each item is saved independently so one failure doesn't block the rest.
  useEffect(() => {
    saveRef.current = async () => {
      const results = await Promise.allSettled(
        entries.map((entry) => {
          const trimmed = entry.handle.trim();
          if (trimmed && !entry.alreadyExists) {
            // New platform entry
            return createUserPlatform({
              platformId: entry.platformId,
              handle: trimmed,
              platformType: entry.platformType,
            });
          } else if (entry.alreadyExists && entry.userPlatformId && trimmed !== (entry.originalHandle || '')) {
            // Existing entry whose handle was changed — an empty handle is a
            // removal and clears the platform server-side.
            return updateUserPlatform(entry.userPlatformId, { handle: trimmed });
          } else if (!trimmed && !entry.userPlatformId && entry.originalHandle) {
            // The user removed a prefilled link that has no DB row yet. Record
            // the removal now so the background discovery task cannot insert
            // the wrong link after this step is saved.
            return createUserPlatform({
              platformId: entry.platformId,
              handle: '',
              platformType: entry.platformType,
            });
          }
          return Promise.resolve();
        })
      );
      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length > 0) {
        // Surface the first error so the user knows something went wrong
        throw (failures[0] as PromiseRejectedResult).reason;
      }
    };
  }, [saveRef, entries]);

  // Dnd sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setEntries((prev) => {
        const oldIndex = prev.findIndex((e) => e.platformId === active.id);
        const newIndex = prev.findIndex((e) => e.platformId === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const updateHandle = (platformId: string, value: string) => {
    setEntries((prev) =>
      prev.map((e) => {
        if (e.platformId !== platformId) return e;
        // extractHandleForDisplay expects { title, baseURL }; PlatformEntry
        // stores the display name in platformName, so map it explicitly.
        // This turns a pasted full URL into the same handle format used by
        // prefill (e.g. Spotify artist ID, Wikipedia article name).
        const handle = extractHandleForDisplay(
          { title: e.platformName, baseURL: e.baseURL },
          value,
        );
        return { ...e, handle };
      })
    );
  };

  const tabEntries = entries.filter((e) => e.platformType === activeTab);

  return (
    <div className="space-y-5 onb-fade-in">
      {/* AI-enhanced indicator — shown when the identity engine prefilled
          platform handles via SERP / Wikipedia. Mirrors the Hero/About steps. */}
      {buildResult?.identity?.aiEnhanced && entries.some((e) => e.autoLinked) && (
        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5 w-fit">
          <span>✨</span> AI-filled links. We encourage you to verify the prefilled handles by visiting each profile before continuing!
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'social-media' | 'audio-streaming')}>
        <TabsList>
          <TabsTrigger value="social-media">Social Media</TabsTrigger>
          <TabsTrigger value="audio-streaming">Audio Streaming</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      ) : tabEntries.length > 0 ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={tabEntries.map((e) => e.platformId)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {tabEntries.map((entry) => (
                <SortableCard
                  key={entry.platformId}
                  entry={entry}
                  onHandleChange={updateHandle}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <p className="text-sm text-muted-foreground text-center py-4">
          No {activeTab === 'social-media' ? 'social media' : 'audio streaming'} platforms configured yet.
        </p>
      )}
    </div>
  );
};

export default PlatformsOnboarding;
